import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, X, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { submitContact } from "@/lib/api";
import LiveStatus from "@/components/site/LiveStatus";

export default function StickyContactBar() {
  const { t, lang } = useLang();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("stickybar-dismissed") === "1") setDismissed(true);
    const onScroll = () => {
      // Show after user scrolls past 60% of the first viewport
      if (window.scrollY > window.innerHeight * 0.6) setVisible(true);
      else setVisible(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("stickybar-dismissed", "1");
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = phone.trim();
    if (clean.length < 7) {
      toast.error(t.stickyBar.invalid);
      return;
    }
    setSending(true);
    try {
      await submitContact({
        name: t.stickyBar.leadName,
        phone: clean,
        message: t.stickyBar.leadMessage,
        type: "callback",
        lang,
      });
      setSent(true);
      toast.success(t.stickyBar.success);
      setTimeout(() => { dismiss(); }, 2200);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(status === 429 ? t.stickyBar.rateLimited : t.stickyBar.error);
    } finally { setSending(false); }
  };

  if (dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 200 }}
          className="fixed bottom-0 inset-x-0 z-40 px-3 pb-3 sm:pb-4"
          data-testid="sticky-contact-bar"
        >
          <div className="max-w-4xl mx-auto rounded-2xl border border-white/10 shadow-2xl backdrop-blur-xl relative overflow-hidden" style={{ background: "rgba(10,17,40,0.85)" }}>
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(0,242,96,0.3), transparent 60%)" }} />
            <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 sm:p-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(0,242,96,0.15)" }}>
                  <Phone size={20} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-extrabold text-sm sm:text-base">{t.stickyBar.title}</div>
                    <LiveStatus compact />
                  </div>
                  <div className="text-xs text-slate-400">{t.stickyBar.subtitle}</div>
                </div>
              </div>
              {sent ? (
                <div className="flex items-center gap-2 text-primary font-bold text-sm px-4 py-2.5">
                  <CheckCircle2 size={18} /> {t.stickyBar.success}
                </div>
              ) : (
                <form onSubmit={submit} className="flex items-center gap-2 shrink-0">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t.stickyBar.placeholder}
                    dir="ltr"
                    className="w-40 sm:w-48 rounded-xl bg-black/40 border border-white/15 px-4 py-2.5 text-sm focus:outline-none focus:border-[color:var(--primary)]"
                    data-testid="sticky-phone-input"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    className="rounded-xl px-4 sm:px-5 py-2.5 text-sm font-bold text-[#04120a] disabled:opacity-60 inline-flex items-center gap-2"
                    style={{ background: "var(--primary)" }}
                    data-testid="sticky-submit"
                  >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    <span className="hidden sm:inline">{t.stickyBar.cta}</span>
                  </button>
                </form>
              )}
              <button
                type="button"
                onClick={dismiss}
                aria-label="close"
                className="absolute top-1.5 end-1.5 sm:relative sm:top-auto sm:end-auto shrink-0 h-8 w-8 rounded-lg grid place-items-center hover:bg-white/10 transition-colors text-slate-400"
                data-testid="sticky-dismiss"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
